import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type RaceTier = "wmm" | "major_local" | "series" | "classic";

export type CatalogBrief = {
  id: number;
  slug: string;
  name: string;
  discipline?: string;            // race discipline (canon disc-colour source)
  city: string | null;
  country: string | null;
  tier: RaceTier;
  registration_url?: string | null;
  registration_status?: string | null;
  is_lottery?: boolean;
};

// Канон один на весь фронт — см. domain/discipline.ts
import type { RaceDiscipline } from "./domain/discipline";
export type { RaceDiscipline };

export type RegistrationStatus = "open" | "lottery" | "closed" | "upcoming" | null;

export type RaceCatalogItem = {
  id: number;
  slug: string;
  name: string;
  distance_km: number;
  discipline: RaceDiscipline;
  typical_month: number | null;
  next_race_date: string | null;
  country: string | null;
  city: string | null;
  tier: RaceTier;
  series_slug: string | null;
  logo_svg: string | null;
  registration_url: string | null;
  registration_status: RegistrationStatus;
  is_lottery: boolean;
};

export type RaceCatalogFilters = {
  tier?: RaceTier;
  discipline?: RaceDiscipline;
  country?: string;
  series?: string;
  q?: string;
  distance_min?: number;
  distance_max?: number;
};

// Sprint 40 — Mini-Fitness goals
export type GoalType = "race" | "weight" | "strength" | "fitness_general" | "hyrox";
export type LiftType = "squat" | "bench" | "deadlift" | "overhead";

export type Goal = {
  id: number | null;
  goal_type: GoalType;
  race_distance: string | null;
  custom_distance_km: number | null;
  d_plus_m: number | null;
  race_date: string | null;
  target_time_seconds: number | null;
  notes: string | null;
  source: string;
  status: GoalStatus | null;
  photo_url: string | null;
  post_race_notes: string | null;
  actual_time_seconds: number | null;
  actual_distance_km: number | null;
  target_weight_kg: number | null;
  start_weight_kg: number | null;
  target_lift_kg: number | null;
  target_lift_type: LiftType | null;
  weekly_sessions_target: number | null;
  future_self_note: string | null;
  created_at: string | null;
  updated_at: string | null;
  race_catalog: CatalogBrief | null;
  // Sess 20 — public commitment (opt-in goal sharing)
  is_public_commitment?: boolean;
  public_commitment_url_slug?: string | null;
  // H2.1 v154 PART2 — chronicle tag + reflection (mirrored from backend GoalRead).
  chronicle_tag?: string | null;
  reflection_text?: string | null;
  // Phase 5 (design-recompose) — periodization toggles
  race_week_mode?: boolean | null;
  extend_taper?: boolean;
  freeze_plan_progression?: boolean;
};

export type CommitmentPublicOut = {
  slug: string;
  share_url: string;
  is_public_commitment: boolean;
};

export type GoalHistoryEntry = {
  id: number;
  event_type: string;
  actor_user_id: number | null;
  accepted_at: string;
  payload: Record<string, unknown>;
};

export type GoalHistory = {
  goal_id: number;
  items: GoalHistoryEntry[];
};

export function useGoalHistory(goalId: number | null | undefined) {
  return useQuery<GoalHistory>({
    queryKey: ["goal-history", goalId],
    queryFn: async () => {
      const r = await api.get<GoalHistory>(`/athlete/goal/${goalId}/history`);
      return r.data;
    },
    enabled: !!goalId,
    staleTime: 60_000,
  });
}

// ── Phase K1 — goal versioning + rollback ───────────────────────────

export type GoalVersionRow = {
  log_id: number;
  action: string;
  actor_role: "athlete" | "coach" | "system";
  actor_user_id: number | null;
  created_at: string;
  has_snapshot: boolean;
  reason: string | null;
};

export type GoalVersions = {
  goal_id: number;
  items: GoalVersionRow[];
};

export function useGoalVersions(goalId: number | null | undefined) {
  return useQuery<GoalVersions>({
    queryKey: ["goal-versions", goalId],
    queryFn: async () => {
      const r = await api.get<GoalVersions>(
        `/athlete/goal/${goalId}/versions`,
      );
      return r.data;
    },
    enabled: !!goalId,
    staleTime: 30_000,
  });
}

export function useRevertGoalToVersion(goalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      logId,
      reason,
    }: {
      logId: number;
      reason: string;
    }) =>
      (
        await api.post<Goal>(
          `/athlete/goal/${goalId}/revert-to/${logId}`,
          { reason },
        )
      ).data,
    onSuccess: () => {
      // Goal mutated → invalidate every derived query
      qc.invalidateQueries({ queryKey: ["athlete-goal"] });
      qc.invalidateQueries({ queryKey: ["goal"] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["goal-journey"] });
      qc.invalidateQueries({ queryKey: ["goal-versions"] });
      qc.invalidateQueries({ queryKey: ["goal-history"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["data-quality"] });
    },
  });
}

export function useMakeGoalPublic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (goalId: number) =>
      (
        await api.post<CommitmentPublicOut>(
          `/athlete/goal/${goalId}/commitment/public`,
          {},
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goal"] });
    },
  });
}

export function useMakeGoalPrivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (goalId: number) => {
      await api.post(`/athlete/goal/${goalId}/commitment/private`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goal"] });
    },
  });
}

export type GoalStatus = "planned" | "active" | "completed" | "failed" | "cancelled";

export type GoalWrite = {
  goal_type?: GoalType;
  race_distance?: string | null;
  custom_distance_km?: number | null;
  // Trail: набір висоти на дистанції (м). Використовується для prognozу,
  // pacing-стратегії та диференціації екіпи (палки, headlamp).
  d_plus_m?: number | null;
  race_date?: string | null;
  target_time_seconds?: number | null;
  notes?: string | null;
  race_catalog_id?: number | null;
  photo_url?: string | null;
  post_race_notes?: string | null;
  target_weight_kg?: number | null;
  start_weight_kg?: number | null;
  target_lift_kg?: number | null;
  target_lift_type?: LiftType | null;
  weekly_sessions_target?: number | null;
  future_self_note?: string | null;
  // Phase 5 (design-recompose) — periodization toggles
  race_week_mode?: boolean | null;
  extend_taper?: boolean;
  freeze_plan_progression?: boolean;
};

export type PostRaceWrite = {
  actual_time_seconds?: number | null;
  actual_distance_km?: number | null;
  photo_url?: string | null;
  post_race_notes?: string | null;
  status?: "completed" | "failed" | "cancelled" | null;
};

export function useGoal() {
  return useQuery({
    queryKey: ["athlete-goal"],
    queryFn: async () => {
      const r = await api.get<Goal | null>("/athlete/goal");
      return r.data;
    },
  });
}

export function useGoalUpsert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GoalWrite) => {
      const r = await api.put<Goal>("/athlete/goal", payload);
      return r.data;
    },
    onSuccess: () => {
      // Z2 sess: comprehensive invalidation — goal changes affect derived queries
      qc.invalidateQueries({ queryKey: ["athlete-goal"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["goal"] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["goal-journey"] });
      qc.invalidateQueries({ queryKey: ["data-quality"] }); // has_active_goal flag may flip
    },
  });
}

export function useGoalDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/athlete/goal");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-goal"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["goal"] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["goal-journey"] });
      qc.invalidateQueries({ queryKey: ["data-quality"] });
    },
  });
}

// ============================================================
// Phase G — race_catalog drift detection + refresh
// ============================================================

export type CatalogDriftField = {
  field: "race_date" | "d_plus_m" | "custom_distance_km";
  goal_value: string | null;
  catalog_value: string | null;
};

export type CatalogDriftReport = {
  has_drift: boolean;
  race_catalog_id: number | null;
  catalog_name: string | null;
  fields: CatalogDriftField[];
  status_override: "cancelled" | "postponed" | "confirmed" | null;
  status_reason: string | null;
  last_synced_at: string | null;
};

export function useCatalogDrift(goalId: number | null | undefined) {
  return useQuery<CatalogDriftReport>({
    queryKey: ["catalog-drift", goalId],
    queryFn: async () => {
      const r = await api.get<CatalogDriftReport>(
        `/athlete/goal/${goalId}/catalog-drift`,
      );
      return r.data;
    },
    enabled: !!goalId,
    // Catalog refreshes on a cron (~daily) — no point polling more often.
    staleTime: 60 * 60 * 1000,
  });
}

export function useRefreshFromCatalog(goalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: CatalogDriftField["field"][]) => {
      const r = await api.post<Goal>(
        `/athlete/goal/${goalId}/refresh-from-catalog`,
        { fields },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-goal"] });
      qc.invalidateQueries({ queryKey: ["catalog-drift"] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["goal-journey"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["goal-history"] });
    },
  });
}


export function useRaceCatalog(filters?: RaceCatalogFilters) {
  return useQuery({
    queryKey: ["race-catalog", filters || {}],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters?.tier) params.tier = filters.tier;
      if (filters?.discipline) params.discipline = filters.discipline;
      if (filters?.country) params.country = filters.country;
      if (filters?.series) params.series = filters.series;
      if (filters?.q) params.q = filters.q;
      if (filters?.distance_min != null) params.distance_min = filters.distance_min;
      if (filters?.distance_max != null) params.distance_max = filters.distance_max;
      const r = await api.get<RaceCatalogItem[]>("/athlete/goal/race-catalog", { params });
      return r.data;
    },
    staleTime: 1000 * 60 * 30,
  });
}


// ============================================================
// Roadmap
// ============================================================

export type GoalBrief = {
  id: number;
  status: GoalStatus;
  goal_type: GoalType;
  race_distance: string | null;
  custom_distance_km: number | null;
  d_plus_m: number | null;
  race_date: string | null;
  target_time_seconds: number | null;
  actual_time_seconds: number | null;
  actual_distance_km: number | null;
  target_weight_kg: number | null;
  start_weight_kg: number | null;
  target_lift_kg: number | null;
  target_lift_type: LiftType | null;
  weekly_sessions_target: number | null;
  future_self_note: string | null;
  notes: string | null;
  photo_url: string | null;
  video_url?: string | null;
  post_race_notes: string | null;
  race_catalog: CatalogBrief | null;
  created_at: string | null;
  updated_at: string | null;
  chronicle_tag: string | null;
  reflection_text: string | null;
};

export type MilestoneItem = {
  code: string;
  title: string;
  description: string;
  target_distance_km: number | null;
  target_time_seconds: number | null;
  is_completed: boolean;
  order: number;
  persisted_id: number | null;
  rationale: string | null;
};

export type PastRaceBrief = {
  id: number;
  name: string;
  race_date: string;
  distance_km: number;
  discipline: string;
  chronicle_tag: string | null;
  reflection_text: string | null;
  carousel_picks: string[] | null;
  finish_time_seconds: number | null;
  city: string | null;
  country: string | null;
  photo_url: string | null;
  video_url: string | null;
  videos: string[];
  notes: string | null;
};

export type RoadmapOut = {
  past: GoalBrief[];
  active: GoalBrief | null;
  future: GoalBrief[];
  milestones: MilestoneItem[];
  past_races: PastRaceBrief[];
};

export function useRoadmap(memorableOnly: boolean = true) {
  return useQuery({
    queryKey: ["goal-roadmap", memorableOnly],
    queryFn: async () => {
      const qs = memorableOnly ? "?memorable_only=true" : "";
      const r = await api.get<RoadmapOut>(`/athlete/goal/roadmap${qs}`);
      return r.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}


// Sprint 37 F5 — fallback suggestions для атлета без active goal.
export type FallbackSuggestion = {
  title: string;
  description: string;
  distance_km: number | null;
  weeks_to_target: number;
  target_date: string;
  rationale: string;
  kind: "shorter" | "easier_pr" | "pushed_back" | "volume_block";
  race_distance: string;
};

export function useFallbackSuggestions(enabled: boolean = true) {
  return useQuery<FallbackSuggestion[]>({
    queryKey: ["goal-fallback-suggestions"],
    queryFn: async () =>
      (await api.get<FallbackSuggestion[]>("/athlete/goal/fallback-suggestions")).data,
    enabled,
    staleTime: 5 * 60_000,
  });
}


export function useAcceptFallbackGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (s: FallbackSuggestion) => {
      // Создаём AthleteGoal через PUT с virtual race (без race_catalog_id).
      const body: any = {
        race_distance: s.race_distance,
        race_date: s.target_date,
        notes: s.title,
      };
      if (s.race_distance === "custom" && s.distance_km) {
        body.custom_distance_km = s.distance_km;
      }
      const r = await api.put("/athlete/goal", body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goal"] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}


export function useGoalPostRacePatch(goalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PostRaceWrite) => {
      const r = await api.patch<Goal>(`/athlete/goal/${goalId}/post-race`, payload);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goal-journey", goalId] });
      qc.invalidateQueries({ queryKey: ["goal-roadmap"] });
      qc.invalidateQueries({ queryKey: ["athlete-goal"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}


// ============================================================
// Journey
// ============================================================

export type RaceReference = {
  distance_km: number;
  time_seconds: number;
  source: string;
  date: string | null;
};

export type JourneyMarker = {
  date: string;
  kind: "threshold_test" | "long_run" | "personal_best" | "race_day";
  title: string;
  value: string | null;
};

export type JourneyOut = {
  goal: GoalBrief;
  period_start: string;
  period_end: string;
  days_total: number;
  best_efforts: RaceReference[];
  markers: JourneyMarker[];
};

export function useGoalJourney(goalId: number | null) {
  return useQuery({
    queryKey: ["goal-journey", goalId],
    queryFn: async () => {
      const r = await api.get<JourneyOut>(`/athlete/goal/journey/${goalId}`);
      return r.data;
    },
    enabled: !!goalId,
    staleTime: 1000 * 60 * 10,
  });
}

// ────────────────────────────────────────────────────────────────────
// Sess 03 / A2 — goal preview (achievability v2 без persistence)
// ────────────────────────────────────────────────────────────────────

export type GoalPreviewFactor = {
  name: string;
  factor_value: number;
  confidence: string;
  tier: number;
  explanation_ru: string;
  bottleneck_severity: number;
};

export type GoalPreviewResult = {
  applicable: boolean;
  has_data: boolean;
  race_distance_km: number;
  race_discipline: string;
  race_date: string | null;
  achievability_pct: number | null;
  achievability_low_pct: number | null;
  achievability_high_pct: number | null;
  label: string | null;
  /**
   * Canon identity state class (v153 P6.18). Replaces legacy
   * label_tone — dropped in v154 PART2 G1 (one release after v153 deploy).
   */
  state_class: "achievable" | "borderline" | "stretch" | null;
  bottleneck_name: string | null;
  bottleneck_explanation: string | null;
  factors: GoalPreviewFactor[];
  weights_version: string | null;
  data_completeness: number | null;
  blockers: string[];
  notes: string[];
};

export type GoalPreviewInput = {
  race_catalog_id: number;
  target_time_seconds?: number | null;
  race_date?: string | null;
};

export function useGoalPreview() {
  return useMutation({
    mutationFn: async (input: GoalPreviewInput): Promise<GoalPreviewResult> => {
      const r = await api.post<GoalPreviewResult>("/athlete/goal/preview", input);
      return r.data;
    },
  });
}


// ─────────────────────────────────────────────────────────────────────
// Phase C (audit v92) — Athlete-side goal review hooks
//
// Inbox + accept/reject/edit-and-accept/timeline. Pairs with Phase B
// coach-side endpoints. When goal.approval_status='pending_athlete_review',
// athlete sees it in inbox + GoalPage banner.
// ─────────────────────────────────────────────────────────────────────

export type GoalApprovalStatus =
  | "active"
  | "pending_athlete_review"
  | "pending_coach_review"
  | "rejected";

export type GoalInboxItem = {
  id: number;
  goal_type: string;
  race_distance: string | null;
  race_date: string | null;
  target_time_seconds: number | null;
  custom_distance_km: number | null;
  race_catalog_id: number | null;
  notes: string | null;
  approval_status: GoalApprovalStatus;
  source: string;
  created_at: string;
  updated_at: string;
  latest_proposal_reason: string | null;
};

export type GoalInboxResponse = { items: GoalInboxItem[] };

export function useGoalInbox() {
  return useQuery<GoalInboxResponse>({
    queryKey: ["athlete", "goal", "inbox"],
    queryFn: async () => {
      const r = await api.get<GoalInboxResponse>("/athlete/goal/inbox");
      return r.data;
    },
    // Refresh inbox every 60s — pending proposals are time-sensitive but
    // not real-time critical. Backend cache is hot via approval_status
    // partial index (aa80).
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useAcceptCoachProposal(goalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post(`/athlete/goal/${goalId}/accept`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete", "goal", "inbox"] });
      qc.invalidateQueries({ queryKey: ["goal"] });
      qc.invalidateQueries({ queryKey: ["athlete", "goal", "timeline", goalId] });
    },
  });
}

export function useRejectCoachProposal(goalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reason: string }) => {
      const r = await api.post(`/athlete/goal/${goalId}/reject`, payload);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete", "goal", "inbox"] });
      qc.invalidateQueries({ queryKey: ["goal"] });
    },
  });
}

export type AthleteEditAndAcceptPayload = {
  race_date?: string | null;
  target_time_seconds?: number | null;
  notes?: string | null;
  reason: string;
};

export function useEditAndAcceptProposal(goalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AthleteEditAndAcceptPayload) => {
      const r = await api.post(
        `/athlete/goal/${goalId}/edit-and-accept`,
        payload,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete", "goal", "inbox"] });
      qc.invalidateQueries({ queryKey: ["goal"] });
      qc.invalidateQueries({ queryKey: ["athlete", "goal", "timeline", goalId] });
    },
  });
}

export type GoalTimelineEntry = {
  id: number;
  actor_role: "athlete" | "coach" | "system";
  actor_user_id: number | null;
  action: "proposed" | "accepted" | "rejected" | "edited" | "reverted"
        | "acknowledged" | "saved" | "completed";
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
};

export function useAthleteGoalTimeline(goalId: number | null) {
  return useQuery<{ goal_id: number; entries: GoalTimelineEntry[] }>({
    queryKey: ["athlete", "goal", "timeline", goalId],
    enabled: goalId != null,
    queryFn: async () => {
      const r = await api.get<{ goal_id: number; entries: GoalTimelineEntry[] }>(
        `/athlete/goal/${goalId}/timeline`,
      );
      return r.data;
    },
    staleTime: 15_000,
  });
}

// ─── P0.1 R7 — upcoming races sidebar ──────────────────────────────

export type UpcomingRace = {
  id: number;
  priority: "A" | "B" | "C" | string;
  race_date: string;
  race_name: string | null;
  race_distance: string | null;
  custom_distance_km: number | null;
  days_until: number;
  target_time_seconds: number | null;
  is_active: boolean;
};

export function useUpcomingRaces() {
  return useQuery<UpcomingRace[]>({
    queryKey: ["athlete", "goal", "upcoming"],
    queryFn: async () => {
      const r = await api.get<UpcomingRace[]>("/athlete/goal/upcoming");
      return r.data;
    },
    staleTime: 60_000,
    retry: false,
  });
}
